import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { ArrowLeft, FileText, Download, Calendar, DollarSign, Tag, User, Link as LinkIcon, Edit, Trash2 } from 'lucide-react';

const VerGasto = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [gasto, setGasto] = useState(null);
  const [archivos, setArchivos] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchGasto();
  }, [id]);

  const fetchGasto = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch gasto
      const gastoRes = await fetch(`${API_URL}/api/gastos/${id}`, { headers });
      if (gastoRes.ok) {
        const data = await gastoRes.json();
        setGasto(data);
      }

      // Fetch archivos
      const archivosRes = await fetch(`${API_URL}/api/gastos/${id}/archivos`, { headers });
      if (archivosRes.ok) {
        const data = await archivosRes.json();
        setArchivos(data);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const handleDownload = async (archivoId, nombreArchivo) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gastos/${id}/archivos/${archivoId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = nombreArchivo;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al descargar el archivo');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Estás seguro de que deseas cancelar este gasto?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gastos/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Gasto cancelado exitosamente');
        navigate('/egresos');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al cancelar el gasto');
    }
  };

  const frecuenciaLabels = {
    mensual: 'Mensual',
    bimestral: 'Bimestral',
    trimestral: 'Trimestral',
    anual: 'Anual'
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  if (!gasto) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="text-center py-12">
          <p className="text-gray-600">Gasto no encontrado</p>
          <button
            onClick={() => navigate('/egresos')}
            className="mt-4 text-indigo-600 hover:text-indigo-800"
          >
            Volver a Egresos
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <button
            onClick={() => navigate('/egresos')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Volver a Egresos
          </button>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{gasto.concepto}</h1>
              <p className="text-gray-600 mt-1">Detalles del gasto</p>
            </div>
            <div className="flex gap-2">
              {gasto.estatus === 'activo' && (
                <>
                  <button
                    onClick={() => navigate(`/egresos/${id}/editar`)}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    <Edit className="h-4 w-4" />
                    Editar
                  </button>
                  <button
                    onClick={handleDelete}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                    Cancelar Gasto
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Main Info Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Monto */}
            <div>
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                <DollarSign className="h-4 w-4" />
                Monto
              </div>
              <p className="text-2xl font-bold text-gray-900">
                ${gasto.monto.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
              </p>
            </div>

            {/* Fecha */}
            <div>
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                <Calendar className="h-4 w-4" />
                Fecha del Gasto
              </div>
              <p className="text-lg font-semibold text-gray-900">
                {new Date(gasto.fecha_gasto).toLocaleDateString('es-MX', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>

            {/* Categoría */}
            <div>
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                <Tag className="h-4 w-4" />
                Categoría
              </div>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                {gasto.categoria_nombre || 'Sin categoría'}
              </span>
            </div>

            {/* Tipo */}
            <div>
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                <FileText className="h-4 w-4" />
                Tipo de Gasto
              </div>
              {gasto.tipo_gasto === 'recurrente' ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  Recurrente
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                  Único
                </span>
              )}
            </div>

            {/* Ligado a */}
            <div>
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                <LinkIcon className="h-4 w-4" />
                Ligado a
              </div>
              <p className="text-gray-900">
                {gasto.ligado_a === 'contrato' && gasto.contrato_numero && (
                  <span>Contrato: {gasto.contrato_numero}</span>
                )}
                {gasto.ligado_a === 'propietario' && gasto.propietario_nombre && (
                  <span>Propietario: {gasto.propietario_nombre}</span>
                )}
                {gasto.ligado_a === 'ninguno' && (
                  <span className="text-gray-500">Gasto General</span>
                )}
              </p>
            </div>

            {/* Estatus */}
            <div>
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                <User className="h-4 w-4" />
                Estatus
              </div>
              {gasto.estatus === 'activo' ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  Activo
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                  Cancelado
                </span>
              )}
            </div>
          </div>

          {/* Descripción */}
          {gasto.descripcion && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Descripción</h3>
              <p className="text-gray-600">{gasto.descripcion}</p>
            </div>
          )}

          {/* Info del registro */}
          <div className="mt-6 pt-6 border-t border-gray-200 text-sm text-gray-500">
            <p>Registrado el: {new Date(gasto.fecha_registro).toLocaleString('es-MX')}</p>
            {gasto.creado_por_nombre && (
              <p>Por: {gasto.creado_por_nombre}</p>
            )}
          </div>
        </div>

        {/* Archivos Adjuntos */}
        {archivos.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Archivos Adjuntos</h2>
            <div className="space-y-3">
              {archivos.map((archivo) => (
                <div
                  key={archivo.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{archivo.nombre_archivo}</p>
                      <p className="text-xs text-gray-500">
                        {(archivo.tamano / 1024).toFixed(2)} KB • {new Date(archivo.fecha_subida).toLocaleDateString('es-MX')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownload(archivo.id, archivo.nombre_archivo)}
                    className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800"
                  >
                    <Download className="h-4 w-4" />
                    Descargar
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default VerGasto;